import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languageSchema, settingsResponseSchema, type Language } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPatch } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';

/** The admin's switches: the public self-registration form (story 33) and the language everyone sees (story 38). */
export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const state = useApi('/settings', settingsResponseSchema);
  const save = useAction((patch: object) => apiPatch('/settings', settingsResponseSchema, patch));
  const [language, setLanguage] = useState<Language | null>(null);

  // Shown optimistically: the box flips at once and the server's answer settles it.
  const [selfRegistration, setSelfRegistration] = useState<boolean | null>(null);
  const serverValue = state.kind === 'ready' ? state.data.settings.selfRegistrationEnabled : null;
  useEffect(() => {
    if (serverValue !== null) setSelfRegistration(serverValue);
  }, [serverValue]);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;
  const checked = selfRegistration ?? state.data.settings.selfRegistrationEnabled;
  const currentLanguage = language ?? state.data.settings.language;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="settings-page">
      <h2 className="text-lg font-semibold text-slate-900">{t('settings.title')}</h2>
      <label className="mt-4 flex items-start gap-3 text-sm text-slate-800">
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={save.busy}
          onChange={(e) => {
            const next = e.target.checked;
            setSelfRegistration(next);
            void save.run({ selfRegistrationEnabled: next }).then((r) => {
              if (r) setSelfRegistration(r.settings.selfRegistrationEnabled);
              else setSelfRegistration(!next);
            });
          }}
          data-testid="settings-self-registration"
        />
        <span>
          <span className="font-medium">{t('settings.selfRegistration')}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{t('settings.selfRegistrationHint', { url: `${window.location.origin}/register` })}</span>
        </span>
      </label>
      <label className="mt-5 block text-sm text-slate-800">
        <span className="font-medium">{t('settings.language')}</span>
        <span className="mt-0.5 block text-xs text-slate-500">{t('settings.languageHint')}</span>
        <select
          className="mt-2 block rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900"
          value={currentLanguage}
          disabled={save.busy}
          onChange={(e) => {
            const next = languageSchema.parse(e.target.value);
            setLanguage(next);
            void save.run({ language: next }).then((r) => {
              // The admin's own screen follows at once; everyone else's on their next load.
              if (r) void i18n.changeLanguage(r.settings.language);
              else setLanguage(null);
            });
          }}
          data-testid="settings-language"
        >
          {languageSchema.options.map((lang) => (
            <option key={lang} value={lang}>
              {t(`settings.languages.${lang}`)}
            </option>
          ))}
        </select>
      </label>
      <div className="mt-3"><ActionError error={save.error} /></div>
    </section>
  );
}
